import React from "react";
import { Star, MessageSquare } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const testimonials = [
  {
    name: "Emily Rodriguez",
    date: "December 10, 2023",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330",
    content: "After a car accident, Kover King's team was there every step of the way. They handled my claim quickly and professionally. I couldn't be more grateful for their support."
  },
  {
    name: "David Thompson",
    date: "December 5, 2023",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e",
    content: "As a small business owner, finding the right insurance coverage was crucial. Kover King provided expert guidance and found comprehensive coverage that protected my business without breaking the bank."
  },
  {
    name: "Sarah Chen",
    date: "December 1, 2023",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80",
    content: "The team at Kover King made getting home insurance so simple. They explained everything clearly and found me great coverage at a competitive rate."
  },
  {
    name: "Michael Foster",
    date: "November 28, 2023",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e",
    content: "I've been with Kover King for over 3 years now. Their customer service is outstanding, and they always go above and beyond to help with claims."
  },
  {
    name: "Jessica Patel",
    date: "November 25, 2023",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2",
    content: "Switching to Kover King was the best decision. They found me better coverage for less money, and their team is always responsive and helpful."
  },
  {
    name: "Robert Kim",
    date: "November 20, 2023",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
    content: "The claims process was smooth and straightforward. Their team kept me informed every step of the way. Highly recommend!"
  },
  {
    name: "Lisa Martinez",
    date: "November 15, 2023",
    avatar: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb",
    content: "As someone new to insurance, I appreciated how patient and thorough they were in explaining my options. Great experience overall!"
  },
  {
    name: "James Wilson",
    date: "November 10, 2023",
    avatar: "https://images.unsplash.com/photo-1463453091185-61582044d556",
    content: "Their online portal makes managing my policies so easy. Plus, their rates are competitive and the coverage is comprehensive."
  }
];

const Testimonials = () => {
  const plugin = React.useMemo(
    () =>
      Autoplay({
        delay: 2000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    []
  );

  return (
    <section className="py-4 sm:py-8 sm:my-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <Badge variant="secondary" className="bg-[#F8F7FF] text-primary text-sm mb-2 flex items-center gap-1 mx-auto w-fit">
            <MessageSquare className="h-4 w-4" />
            Testimonials
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold mt-2 px-2">
            What our clients are <span className="text-primary">saying</span>
          </h2>
          <p className="text-gray-600 mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base px-2">
            Our insurance agency is transforming the way people protect what matters most. Here's what some of our clients have to say about their experience.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-start">
          {/* Left Column - Stats Card */}
          <div className="bg-[#F8F7FF] p-8 sm:p-12 rounded-xl flex flex-col items-center justify-center">
            <div className="text-4xl sm:text-6xl font-bold text-primary mb-3 sm:mb-4">2000+</div>
            <div className="text-lg sm:text-xl text-gray-700">Trusted by clients</div>
          </div>

          {/* Right Column - Testimonials Carousel */}
          <div className="lg:col-span-2">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[plugin]}
              className="w-full"
            >
              <CarouselContent>
                {testimonials.map((testimonial, index) => (
                  <CarouselItem key={index}>
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 sm:h-12 sm:w-12">
                            <AvatarImage 
                              src={testimonial.avatar} 
                              alt={testimonial.name}
                              className="object-cover w-full h-full"
                            />
                            <AvatarFallback>{testimonial.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-sm sm:text-base">{testimonial.name}</h3>
                            <p className="text-xs sm:text-sm text-gray-500">{testimonial.date}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-yellow-400" />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm sm:text-base">{testimonial.content}</p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

